import { useState, useEffect, useCallback } from 'react';
import { api } from '../api';
import { BRAND } from '../config/brand';

const G = BRAND.colors.primary;

// ─── POList ───────────────────────────────────────────────────────────────────
// Index page for purchase orders. Shows PO#, supplier, client, total, status,
// in-hand date. Filterable by status. Click row → navigate to detail.
//
// USAGE: route this at /pos
//   <POList getToken={getToken} navigate={navigate} showToast={showToast} />
export default function POList({ getToken, navigate, showToast }) {
  const [pos, setPos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [search, setSearch] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const token = await getToken();
      const params = new URLSearchParams();
      if (statusFilter) params.set('status', statusFilter);
      if (search.trim()) params.set('q', search.trim());
      const qs = params.toString();
      const data = await api.withToken(token).getPOs(qs || null);
      setPos(data || []);
    } catch (e) {
      showToast?.(e.message || 'Failed to load POs', 'error');
    } finally {
      setLoading(false);
    }
  }, [getToken, statusFilter, search, showToast]);

  useEffect(() => { load(); }, [load]);

  const goTo = (id) => navigate?.(`pos/${id}`);

  const deleteRow = async (e, p) => {
    e.stopPropagation(); // don't navigate into the row
    if (!window.confirm(`Delete ${p.po_number}? This cannot be undone.`)) return;
    try {
      const token = await getToken();
      await api.withToken(token).deletePO(p.id);
      setPos(prev => prev.filter(x => x.id !== p.id));
      showToast?.(`${p.po_number} deleted`, 'success');
    } catch (err) {
      showToast?.(err.message || 'Delete failed', 'error');
    }
  };

  const counts = pos.reduce((acc, p) => {
    acc[p.status] = (acc[p.status] || 0) + 1;
    return acc;
  }, {});

  return (
    <div style={{ maxWidth: 1100, margin: '0 auto', padding: 24 }}>
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: 20 }}>
        <h1 style={{ margin: 0, fontSize: 24, flex: 1 }}>Purchase Orders</h1>
      </div>

      {/* Filters */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 20, alignItems: 'center' }}>
        <input
          placeholder="Search by PO#, supplier, or client…"
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{
            flex: 1, padding: '10px 14px', border: '1px solid #cbd5e1',
            borderRadius: 6, fontSize: 14,
          }}
        />
        <div style={{ display: 'flex', gap: 4, fontSize: 13 }}>
          {[
            { key: '', label: 'All', count: pos.length },
            { key: 'draft', label: 'Draft', count: counts.draft || 0 },
            { key: 'sent', label: 'Sent', count: counts.sent || 0 },
            { key: 'confirmed', label: 'Confirmed', count: counts.confirmed || 0 },
            { key: 'cancelled', label: 'Cancelled', count: counts.cancelled || 0 },
          ].map(f => (
            <button
              key={f.key}
              onClick={() => setStatusFilter(f.key)}
              style={{
                padding: '8px 12px', borderRadius: 6, fontSize: 13,
                border: '1px solid ' + (statusFilter === f.key ? G : '#e2e8f0'),
                background: statusFilter === f.key ? G : '#fff',
                color: statusFilter === f.key ? '#fff' : '#334155',
                cursor: 'pointer', fontWeight: 500,
              }}
            >
              {f.label}{f.count ? ` (${f.count})` : ''}
            </button>
          ))}
        </div>
      </div>

      {/* Table */}
      {loading ? (
        <div style={{ padding: 40, textAlign: 'center', color: '#64748b' }}>Loading POs…</div>
      ) : pos.length === 0 ? (
        <div style={{
          padding: '36px 28px', textAlign: 'center', color: '#64748b',
          background: '#f8fafc', border: '1px dashed #cbd5e1', borderRadius: 10,
        }}>
          <div style={{ fontSize: 28, marginBottom: 10 }}>📦</div>
          <div style={{ fontSize: 15, fontWeight: 600, color: '#0F172A', marginBottom: 6 }}>
            No purchase orders yet
          </div>
          <div style={{ fontSize: 13, color: '#64748b', maxWidth: 460, margin: '0 auto', lineHeight: 1.6 }}>
            Purchase orders are created from approved presentations. Build a presentation, get client approval, then generate the PO with one click — pre-filled with supplier info, items, and ship-to.
          </div>
          <button onClick={() => navigate?.('presentations')}
            style={{
              marginTop: 16, background: '#10b981', border: 'none', borderRadius: 8,
              padding: '9px 18px', fontSize: 13, fontWeight: 500, color: '#fff',
              cursor: 'pointer', fontFamily: 'inherit',
            }}>
            Go to Presentations →
          </button>
        </div>
      ) : (
        <div style={{
          background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8,
          overflow: 'hidden',
        }}>
          <div style={{
            display: 'grid',
            gridTemplateColumns: '110px 1.5fr 1.5fr 110px 110px 100px 40px',
            gap: 16,
            padding: '10px 16px', background: '#f8fafc',
            fontSize: 11, fontWeight: 700, textTransform: 'uppercase',
            color: '#64748b', borderBottom: '1px solid #e2e8f0',
          }}>
            <div>PO #</div>
            <div>Supplier</div>
            <div>Client</div>
            <div style={{ textAlign: 'right' }}>Total</div>
            <div>In-hand</div>
            <div>Status</div>
            <div></div>
          </div>
          {pos.map(p => (
            <div
              key={p.id}
              onClick={() => goTo(p.id)}
              style={{
                display: 'grid',
                gridTemplateColumns: '110px 1.5fr 1.5fr 110px 110px 100px 40px',
                gap: 16,
                padding: '14px 16px', fontSize: 13,
                borderBottom: '1px solid #f1f5f9',
                cursor: 'pointer',
                alignItems: 'center',
              }}
              onMouseOver={e => e.currentTarget.style.background = '#f8fafc'}
              onMouseOut={e => e.currentTarget.style.background = '#fff'}
            >
              <div style={{ fontWeight: 600 }}>{p.po_number}</div>
              <div>{p.supplier_name}</div>
              <div style={{ color: '#64748b' }}>{p.client_name || '—'}</div>
              <div style={{ textAlign: 'right', fontWeight: 600 }}>${Number(p.total).toFixed(2)}</div>
              <div style={{ color: '#64748b' }}>
                {p.in_hand_date ? new Date(p.in_hand_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '—'}
              </div>
              <div><StatusBadge status={p.status} /></div>
              <div style={{ textAlign: 'center' }}>
                <button
                  onClick={e => deleteRow(e, p)}
                  title="Delete PO"
                  style={{
                    border: 'none', background: 'transparent', color: '#cbd5e1',
                    cursor: 'pointer', fontSize: 18, lineHeight: 1, padding: 4,
                  }}
                  onMouseOver={e => e.currentTarget.style.color = '#dc2626'}
                  onMouseOut={e => e.currentTarget.style.color = '#cbd5e1'}
                >
                  ×
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function StatusBadge({ status }) {
  const styles = {
    draft:     { bg: '#f1f5f9', fg: '#475569' },
    sent:      { bg: '#dbeafe', fg: '#1e40af' },
    confirmed: { bg: '#dcfce7', fg: '#166534' },
    cancelled: { bg: '#fee2e2', fg: '#991b1b' },
  };
  const s = styles[status] || styles.draft;
  return (
    <span style={{
      background: s.bg, color: s.fg, padding: '3px 10px',
      borderRadius: 12, fontSize: 11, fontWeight: 600,
      textTransform: 'capitalize',
    }}>{status}</span>
  );
}
